/* Room detection (DOM-free): the enclosed regions of the wall graph.
   Walls are split where they meet (welded corners, T-junctions, crossings),
   the result is a planar graph, and its bounded faces are the rooms. Each
   room gets a centerline polygon, an approximate inside area (walls have
   thickness), and a label point well inside it. */
import type { Pt, Wall } from './types.ts';
import { WELD_TOL } from './geom.ts';

export interface Room {
  /** simplified centerline polygon (spikes and collinear points removed) */
  pts: Pt[];
  /** centerline area (sq in) and the approximate face-to-face inside area */
  area: number; inside: number;
  /** a good spot for a label: deep inside the room */
  cx: number; cy: number;
  x1: number; y1: number; x2: number; y2: number;
  /** axis-aligned rectangle? then inside w × h are exact */
  rect: boolean; w: number; h: number;
  wallIds: string[];
}

const TOL = WELD_TOL;
/** Faces smaller than this (sq in) are slivers, not rooms. */
const MIN_AREA = 4 * 144;

interface Edge { u: number; v: number; th: number; wallId: string }

export function detectRooms(walls: Wall[]): Room[] {
  /* ---- nodes: endpoints snapped together ---- */
  const nodes: Pt[] = [];
  const nodeAt = (x: number, y: number): number => {
    for (let i = 0; i < nodes.length; i++) if (Math.hypot(nodes[i].x - x, nodes[i].y - y) < TOL) return i;
    nodes.push({ x, y }); return nodes.length - 1;
  };
  const segs = walls
    .filter(w => Math.hypot(w.x2 - w.x1, w.y2 - w.y1) >= TOL)
    .map(w => ({ w, a: nodeAt(w.x1, w.y1), b: nodeAt(w.x2, w.y2), cuts: [] as { t: number; n: number }[] }));
  const along = (s: typeof segs[number], p: Pt) => {   // distance along + perpendicular distance
    const A = nodes[s.a], B = nodes[s.b], dx = B.x - A.x, dy = B.y - A.y, L = Math.hypot(dx, dy);
    const t = ((p.x - A.x) * dx + (p.y - A.y) * dy) / L;
    return { t, d: Math.abs((p.x - A.x) * dy - (p.y - A.y) * dx) / L, L };
  };
  /* ---- crossings: walls that pass through each other get a node there ---- */
  for (let i = 0; i < segs.length; i++) for (let j = i + 1; j < segs.length; j++) {
    const P = nodes[segs[i].a], Q = nodes[segs[i].b], R = nodes[segs[j].a], S = nodes[segs[j].b];
    const d = (Q.x - P.x) * (S.y - R.y) - (Q.y - P.y) * (S.x - R.x);
    if (Math.abs(d) < 1e-9) continue;                                   // parallel
    const t = ((R.x - P.x) * (S.y - R.y) - (R.y - P.y) * (S.x - R.x)) / d;
    const u = ((R.x - P.x) * (Q.y - P.y) - (R.y - P.y) * (Q.x - P.x)) / d;
    if (t <= 0 || t >= 1 || u <= 0 || u >= 1) continue;
    const x = P.x + t * (Q.x - P.x), y = P.y + t * (Q.y - P.y);
    const LPQ = Math.hypot(Q.x - P.x, Q.y - P.y), LRS = Math.hypot(S.x - R.x, S.y - R.y);
    if (t * LPQ < TOL || (1 - t) * LPQ < TOL || u * LRS < TOL || (1 - u) * LRS < TOL) continue;   // that's a corner/T, handled below
    nodeAt(x, y);
  }
  /* ---- T-junctions and crossings: every node lying on a wall's interior splits it ---- */
  for (const s of segs) for (let n = 0; n < nodes.length; n++) {
    if (n === s.a || n === s.b) continue;
    const r = along(s, nodes[n]);
    if (r.d < TOL && r.t > TOL && r.t < r.L - TOL) s.cuts.push({ t: r.t, n });
  }
  /* ---- edges (deduplicated: two rooms sharing a wall, or overlapping walls) ---- */
  const edgeKey = new Map<string, Edge>();
  for (const s of segs) {
    const chain = [s.a, ...s.cuts.sort((p, q) => p.t - q.t).map(c => c.n), s.b];
    for (let i = 0; i + 1 < chain.length; i++) {
      const u = chain[i], v = chain[i + 1];
      if (u === v) continue;
      const k = u < v ? `${u}:${v}` : `${v}:${u}`;
      if (!edgeKey.has(k)) edgeKey.set(k, { u, v, th: s.w.th, wallId: s.w.id });
    }
  }
  const edges = [...edgeKey.values()];
  /* ---- planar graph: neighbours sorted by angle ---- */
  const adj = new Map<number, number[]>();
  for (const e of edges) {
    (adj.get(e.u) || adj.set(e.u, []).get(e.u)!).push(e.v);
    (adj.get(e.v) || adj.set(e.v, []).get(e.v)!).push(e.u);
  }
  const ang = (a: number, b: number) => Math.atan2(nodes[b].y - nodes[a].y, nodes[b].x - nodes[a].x);
  for (const [v, ns] of adj) ns.sort((p, q) => ang(v, p) - ang(v, q));
  const edgeOf = (u: number, v: number) => edgeKey.get(u < v ? `${u}:${v}` : `${v}:${u}`)!;
  /* ---- faces: walk each directed edge, always turning to the next neighbour in angle order ---- */
  const seen = new Set<string>();
  const rooms: Room[] = [];
  for (const e of edges) for (const [su, sv] of [[e.u, e.v], [e.v, e.u]]) {
    if (seen.has(`${su}>${sv}`)) continue;
    const poly: number[] = [];
    const used: Edge[] = [];
    let u = su, v = sv, guard = 0;
    while (!seen.has(`${u}>${v}`) && guard++ < 10000) {
      seen.add(`${u}>${v}`);
      poly.push(v); used.push(edgeOf(u, v));
      const ns = adj.get(v)!;
      const i = ns.indexOf(u);
      const next = ns[(i + 1) % ns.length];
      u = v; v = next;
    }
    const pts = poly.map(i => nodes[i]);
    let area = 0;
    for (let i = 0; i < pts.length; i++) { const p = pts[i], q = pts[(i + 1) % pts.length]; area += p.x * q.y - q.x * p.y; }
    area = -area / 2;                    // y-down + this turn rule: bounded faces are negative; flip so rooms are positive
    if (area <= MIN_AREA) continue;      // the outer face (now negative) or a sliver
    const simple = simplify(pts);
    if (simple.length < 3) continue;
    const th = used.reduce((s, x) => s + x.th, 0) / used.length;
    const perim = simple.reduce((s, p, i) => { const q = simple[(i + 1) % simple.length]; return s + Math.hypot(q.x - p.x, q.y - p.y); }, 0);
    const xs = simple.map(p => p.x), ys = simple.map(p => p.y);
    const x1 = Math.min(...xs), x2 = Math.max(...xs), y1 = Math.min(...ys), y2 = Math.max(...ys);
    const rect = simple.length === 4 && simple.every((p, i) => { const q = simple[(i + 1) % 4]; return Math.abs(p.x - q.x) < TOL || Math.abs(p.y - q.y) < TOL; });
    const label = pole(simple, x1, y1, x2, y2);
    rooms.push({
      pts: simple, area, inside: Math.max(0, area - perim * th / 2 + th * th),
      cx: label.x, cy: label.y, x1, y1, x2, y2, rect,
      w: rect ? x2 - x1 - th : 0, h: rect ? y2 - y1 - th : 0,
      wallIds: [...new Set(used.map(x => x.wallId))],
    });
  }
  return rooms.sort((a, b) => a.y1 - b.y1 || a.x1 - b.x1);
}

/** Drop repeated points, spikes (dead-end walls walked out and back) and collinear points. */
function simplify(pts: Pt[]): Pt[] {
  let out = pts.slice();
  for (let pass = 0; pass < 8; pass++) {
    const n = out.length, keep: Pt[] = [];
    let changed = false;
    for (let i = 0; i < n; i++) {
      const p = out[(i - 1 + n) % n], c = out[i], q = out[(i + 1) % n];
      const cross = (c.x - p.x) * (q.y - c.y) - (c.y - p.y) * (q.x - c.x);
      const same = (a: Pt, b: Pt) => Math.hypot(a.x - b.x, a.y - b.y) < TOL;
      if (same(c, q) || same(p, q) || Math.abs(cross) < 1e-6) { changed = true; continue; }   // dup, spike tip, or straight-through
      keep.push(c);
    }
    out = keep;
    if (!changed) break;
  }
  return out;
}

/** Where a room's size text goes: under the room's own label if there is
    exactly one inside it, otherwise at the room's label spot. Returns the
    label too so the caller can leave room for its height. */
export function labelAnchor<L extends Pt>(room: Room, labels: L[]): { x: number; y: number; under: L | null } {
  const own = labels.filter(l => l.x >= room.x1 && l.x <= room.x2 && l.y >= room.y1 && l.y <= room.y2 && inside(l, room.pts));
  if (own.length === 1) return { x: own[0].x, y: own[0].y, under: own[0] };
  return { x: room.cx, y: room.cy, under: null };
}

const inside = (p: Pt, poly: Pt[]): boolean => {
  let on = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i], b = poly[j];
    if ((a.y > p.y) !== (b.y > p.y) && p.x < (b.x - a.x) * (p.y - a.y) / (b.y - a.y) + a.x) on = !on;
  }
  return on;
};
const distToPoly = (p: Pt, poly: Pt[]): number => {
  let best = Infinity;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length];
    const dx = b.x - a.x, dy = b.y - a.y, l2 = dx * dx + dy * dy;
    const t = l2 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2)) : 0;
    best = Math.min(best, Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy)));
  }
  return best;
};
/** A point deep inside the polygon (coarse pole of inaccessibility; exact center for rectangles). */
function pole(poly: Pt[], x1: number, y1: number, x2: number, y2: number): Pt {
  let best: Pt = { x: (x1 + x2) / 2, y: (y1 + y2) / 2 }, score = inside(best, poly) ? distToPoly(best, poly) : -1;
  const N = 16;
  for (let i = 1; i < N; i++) for (let j = 1; j < N; j++) {
    const p = { x: x1 + (x2 - x1) * i / N, y: y1 + (y2 - y1) * j / N };
    if (!inside(p, poly)) continue;
    const d = distToPoly(p, poly);
    if (d > score + 1e-6) { score = d; best = p; }
  }
  return best;
}

/** Walls on the outside of the plan — the ones an upper floor usually
    shares. A wall is exterior when some stretch of it has no room on one
    side. With no rooms at all, every wall counts. */
export function exteriorWalls(walls: Wall[]): Wall[] {
  const rooms = detectRooms(walls);
  if (!rooms.length) return walls.slice();
  const inAnyRoom = (p: Pt) => rooms.some(r => p.x >= r.x1 && p.x <= r.x2 && p.y >= r.y1 && p.y <= r.y2 && inside(p, r.pts));
  return walls.filter(w => {
    const dx = w.x2 - w.x1, dy = w.y2 - w.y1, L = Math.hypot(dx, dy) || 1;
    const nx = -dy / L, ny = dx / L, off = w.th / 2 + 2;
    for (const t of [0.25, 0.5, 0.75]) {
      const x = w.x1 + dx * t, y = w.y1 + dy * t;
      if (!inAnyRoom({ x: x + nx * off, y: y + ny * off }) || !inAnyRoom({ x: x - nx * off, y: y - ny * off })) return true;
    }
    return false;
  });
}
